import mapboxgl, { Map as MapboxMap, Marker } from "mapbox-gl";
import type { MapFeedItemDTO } from "../../data/types";
import { createVenuePinEl, updateVenuePinEl } from "./venuePin";
import { hasVenueCoords, isVenueVisibleOnMap } from "./mapVisibility";

type MarkerItem = {
  id: string;
  lat: number;
  lon: number;
  item: MapFeedItemDTO;
};

type SyncVenueMarkersOptions = {
  map: MapboxMap;
  items: MapFeedItemDTO[];
  venueMarkers: globalThis.Map<string, Marker>;
  feedByVenueId: globalThis.Map<string, MapFeedItemDTO>;
  onSelect: (item: MapFeedItemDTO) => void;
};

export function clearVenueMarkers(
  venueMarkers: globalThis.Map<string, Marker>,
  feedByVenueId: globalThis.Map<string, MapFeedItemDTO>
) {
  venueMarkers.forEach((marker) => marker.remove());
  venueMarkers.clear();
  feedByVenueId.clear();
}

export function syncVenueMarkers({
  map,
  items,
  venueMarkers,
  feedByVenueId,
  onSelect,
}: SyncVenueMarkersOptions) {
  // Filtrera här och inte bara i API:t. Markörer för barer som fallit ur
  // listan städas bort längre ned, så en bar som slutar spela försvinner.
  const nextItems: MarkerItem[] = items
    .filter(isVenueVisibleOnMap)
    .filter(hasVenueCoords)
    .map((item) => ({
      id: item.venue.id,
      lat: item.venue.lat,
      lon: item.venue.lon,
      item,
    }));

  const nextIds = new Set(nextItems.map((item) => item.id));

  feedByVenueId.clear();

  for (const item of nextItems) {
    feedByVenueId.set(item.id, item.item);
  }

  venueMarkers.forEach((marker, id) => {
    if (!nextIds.has(id)) {
      marker.remove();
      venueMarkers.delete(id);
    }
  });

  for (const markerItem of nextItems) {
    const existing = venueMarkers.get(markerItem.id);

    if (!existing) {
      const el = createVenuePinEl({
        item: markerItem.item,
        venueId: markerItem.id,
        getCurrentItem: (venueId) => feedByVenueId.get(venueId),
        onSelect,
      });

      const marker = new mapboxgl.Marker({
        element: el,
        anchor: "bottom",
      })
        .setLngLat([markerItem.lon, markerItem.lat])
        .addTo(map);

      venueMarkers.set(markerItem.id, marker);
      continue;
    }

    const currentLngLat = existing.getLngLat();

    if (
      currentLngLat.lng !== markerItem.lon ||
      currentLngLat.lat !== markerItem.lat
    ) {
      existing.setLngLat([markerItem.lon, markerItem.lat]);
    }

    updateVenuePinEl(existing.getElement(), markerItem.item);
  }
}
